/**
 * Pure deep-link helpers for the VI pipeline.
 *
 *  • pickDeepLinkId    — choose the id to search by (tab-appropriate, with fallback)
 *  • deepLinkSearchPatch — the filter-overwrite patch applied to usePipelineStore
 *  • DEEP_LINK_FILTER_REGISTRY / applyRegistry — extra_params → filter setters
 *
 * Window-free + store-free so it unit-tests under the `node` vitest env and so
 * usePipelineStore can import deepLinkSearchPatch without a cycle.
 */
import type { PipelineTab } from '../types';
// Type-only import — erased at compile time, so no runtime cycle with the store.
import type { SearchScope } from '../store/usePipelineStore';

const QUOTATION_QUERY_ID = 'quotation_query_id';
const QUOTATION_ID = 'quotation_id';
const ID = 'id';

/** Non-empty trimmed param, or null. */
const nonEmpty = (v: string | null): string | null => {
  const t = v?.trim();
  return t ? t : null;
};

/**
 * Pick the record id to search by. The path already chose the tab, so prefer
 * the tab-appropriate id, then fall back to whichever is present. BE
 * target_urls currently carry the record id as a plain `id` param, so it's
 * the final fallback for every tab.
 */
export function pickDeepLinkId(params: URLSearchParams, tab: PipelineTab): string | null {
  const quotationQueryId = nonEmpty(params.get(QUOTATION_QUERY_ID));
  const quotationId = nonEmpty(params.get(QUOTATION_ID));
  const genericId = nonEmpty(params.get(ID));
  if (tab === 'all_policies') return quotationId ?? quotationQueryId ?? genericId;
  return quotationQueryId ?? quotationId ?? genericId;
}

export interface DeepLinkSearchPatch {
  searchByScope: Record<SearchScope, string>;
  ownerFilter: number[];
  deepLinkId: string;
}

/**
 * Scopes a deep-link tab targets. Leads writes both sub-tab scopes (the
 * notification can't know whether New Leads or Renewals holds the record);
 * every policy stage tab writes only `policies` — so a deep-link into one
 * pipeline never plants a search in the other.
 */
export function deepLinkScopes(tab: PipelineTab): SearchScope[] {
  return tab === 'leads' ? ['new_leads', 'renewals'] : ['policies'];
}

/**
 * The overwrite semantics for a deep-link lookup: set search on the target
 * tab's scope(s) only (Leads and Policies searches stay independent), clear
 * the owner filter, and remember the id as `deepLinkId`. The stored date range
 * is left alone; `usePipelineFilterParams` omits `created_at__gte/__lte` only
 * while a scope's search still equals `deepLinkId`, so the record is found
 * regardless of age but the moment the search changes, is cleared, or another
 * tab (whose search is not the id) is opened, the date bounds apply again.
 * Previously this emptied the range itself, and nothing ever restored it —
 * every later tab switch went to the API with no date filter at all.
 */
export function deepLinkSearchPatch(
  id: string,
  tab: PipelineTab,
  currentSearchByScope: Record<SearchScope, string>,
): DeepLinkSearchPatch {
  const searchByScope = { ...currentSearchByScope };
  for (const scope of deepLinkScopes(tab)) searchByScope[scope] = id;
  return {
    searchByScope,
    ownerFilter: [],
    deepLinkId: id,
  };
}

/**
 * Maps an `extra_params` key → a setter that writes to the appropriate store.
 *
 * v1: intentionally EMPTY. `extra_params` are forwarded onto the URL; only keys
 * registered here also drive filters. Add an entry when the backend pins a key,
 * e.g. (no tray change needed):
 *
 *   import { useNewLeadsPipelineStore } from '@/modules/voluntary-insurance/leads/new-leads';
 *   manual_quotation_status: (v) =>
 *     useNewLeadsPipelineStore.getState().setFilters({ manual_quotation_status: [v] as never }),
 */
export const DEEP_LINK_FILTER_REGISTRY: Record<string, (value: string) => void> = {};

/** Apply every URL param whose key is registered; ignore the rest. */
export function applyRegistry(
  params: URLSearchParams,
  registry: Record<string, (value: string) => void> = DEEP_LINK_FILTER_REGISTRY,
): void {
  for (const key of Object.keys(registry)) {
    const value = params.get(key);
    if (value !== null) registry[key](value);
  }
}
